import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Using d3js transitions in React",
  "description": "",
  "published": "2016-04-28T00:00:00.000Z",
  "redirect_from": ["/blog/using-d3js-transitions-in-react/swizec/6797"],
  "categories": "animations, d3js, Front End, react, react+d3js, svg, Technical, transitions",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "alphabet_2"
    }}><img parentName="h1" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/04/alphabet_2.gif",
        "alt": "alphabet_2"
      }}></img></h1>
    <p>{`One of d3’s biggest selling points is the general update pattern. Add some function calls and voila – animated visualization with transitions.`}</p>
    <p>{`You can see it in action in Mike Bostock’s `}<a parentName="p" {...{
        "href": "https://bl.ocks.org/mbostock/a8a5baa4c4a470cda598"
      }}>{`General Update Pattern 4.0`}</a>{` where he animates an alphabet. Update the alphabet and old letters transition out, new letters transition in, updated letters transition into a new position. It works like magic.`}</p>
    <p>{`D3 always works like magic …`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`.`}<a parentName="p" {...{
          "href": "https://twitter.com/Swizec"
        }}>{`@Swizec`}</a>{` also with React you're not *binding the data*. you're just iterating over the data and creating SVG elements. It's not data-driven`}</p>{`— Luca Matteis (@lmatteis) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/lmatteis/status/721351646907863040"
      }}>{`April 16, 2016`}</a></blockquote>
    <p>{`You totally `}<em parentName="p">{`can`}</em>{` achieve the same result with React. Yes, even if you use React to render SVG. Yes, you can use d3’s powerful transitions API too.`}</p>
    <p>{`The React version is easier to understand, too. Keep reading and you’ll see why.`}</p>
    <h2 {...{
      "id": "enter-update-exit-transitions-in-react"
    }}>{`Enter update exit transitions in React`}</h2>
    <p>{`We’re going to build that same animated alphabet. To keep things short, this tutorial focuses on the alphabet itself and ignores all the config and parent components.`}</p>
    <p>{`The code is based on React 15 and D3 4.0.0-alpha.29. Don’t worry, it works with the stable version of D3. I’ll explain the subtle differences.`}</p>
    <p>{`To make an animated alphabet, we need two components:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`Alphabet`}</inlineCode>{`, which creates random lists of letters every 1.5 seconds, then maps through them to render `}<inlineCode parentName="li">{`Letter`}</inlineCode>{` components`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`Letter`}</inlineCode>{`, which renders an SVG text element, and takes care of its enter/update/exit transitions`}</li>
    </ul>
    <p>{`We’re going to use React to render SVG elements, and d3 for transitions, intervals, and some maths.`}</p>
    <p>{`You can see the full code on GitHub `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/react-d3-enter-exit-transitions"
      }}>{`here`}</a>{`.`}</p>
    <h2 {...{
      "id": "the-alphabet-component"
    }}>{`The Alphabet component`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`Alphabet`}</inlineCode>{` component holds the current list of letters in state and renders a collection of `}<inlineCode parentName="p">{`Letter`}</inlineCode>{` components in a loop.`}</p>
    <p>{`We start with a skeleton like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/index.jsx

import React, { Component } from "react";
import ReactTransitionGroup from "react-addons-transition-group";
import d3 from "d3";

require("./style.less");

import Letter from "./Letter";

class Alphabet extends Component {
  static letters = "abcdefghijklmnopqrstuvwxyz".split("");
  state = { alphabet: [] };

  componentWillMount() {
    // starts an interval to update alphabet
  }

  render() {
    // spits out svg elements
  }
}

export default Alphabet;
`}</code></pre>
    <p>{`We import our dependencies, add some styling, and define the `}<inlineCode parentName="p">{`Alphabet`}</inlineCode>{` component. It holds a list of available letters in a static `}<inlineCode parentName="p">{`letters`}</inlineCode>{` property, and an empty `}<inlineCode parentName="p">{`alphabet`}</inlineCode>{` in component state. We’ll need a `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` and a `}<inlineCode parentName="p">{`render`}</inlineCode>{` method as well.`}</p>
    <p><em parentName="p">{`Sidenote`}</em>{`: That `}<inlineCode parentName="p">{`require('style.less')`}</inlineCode>{` isn’t key to this tutorial. I’m not a fan of inline styling, so I like to add `}<inlineCode parentName="p">{`less`}</inlineCode>{` compilation to Webpack and include stylesheets like this.`}</p>
    <p>{`To showcase enter-update-exit transitions, we want to create a new alphabet every couple of seconds. The best place to do that is in `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/index.jsx

    componentWillMount() {
        d3.interval(() => this.setState({
           alphabet: d3.shuffle(Alphabet.letters)
                       .slice(0, Math.floor(Math.random() * Alphabet.letters.length))
                       .sort()
        }), 1500);
    }
`}</code></pre>
    <p>{`We use `}<inlineCode parentName="p">{`d3.interval( //.., 1500)`}</inlineCode>{` to call a function every 1.5 seconds. It’s the same as `}<inlineCode parentName="p">{`setInterval`}</inlineCode>{`, but friendlier to batteries and CPUs. On each period, we shuffle the available letters, slice out a random amount, sort them, then update component state with `}<inlineCode parentName="p">{`setState`}</inlineCode>{`.`}</p>
    <p>{`This ensures our alphabet is both random and in alphabetical order.`}</p>
    <p>{`Starting the interval in `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` ensures it only runs when the component is on the page, and that everything’s ready by the time our component renders. It’s good practice to explicitly stop these sorts of intervals in `}<inlineCode parentName="p">{`componentWillUnmount`}</inlineCode>{`.`}</p>
    <p>{`Our declarative transitions magic starts in the `}<inlineCode parentName="p">{`render`}</inlineCode>{` method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/index.jsx

    render() {
        let transform = \`translate(\${this.props.x}, \${this.props.y})\`;

        return (
            <g transform={transform}>
                <reacttransitiongroup component="g">
                    {this.state.alphabet.map((d, i) => (
                        <letter d={d} i={i} key={\`letter-\${d}\`}>
                     ))}
                </letter></reacttransitiongroup>
            </g>
        );
    }
`}</code></pre>
    <p>{`We start with an SVG transformation that moves our alphabet into the specified `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` position, then define a `}<inlineCode parentName="p">{`ReactTransitionGroup`}</inlineCode>{` and map through `}<inlineCode parentName="p">{`this.state.alphabet`}</inlineCode>{` to render a collection of `}<inlineCode parentName="p">{`Letter`}</inlineCode>{` components. Each `}<inlineCode parentName="p">{`Letter`}</inlineCode>{` gets its current text – `}<inlineCode parentName="p">{`d`}</inlineCode>{` – and index – `}<inlineCode parentName="p">{`i`}</inlineCode>{`. We `}<em parentName="p">{`have to`}</em>{` define the `}<inlineCode parentName="p">{`key`}</inlineCode>{` attribute based on the letter – `}<inlineCode parentName="p">{`d`}</inlineCode>{`.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`Alphabet`}</inlineCode>{` component assumes the parent component renders it inside an `}<inlineCode parentName="p">{`<svg>`}</inlineCode>{` tag.`}</p>
    <h3 {...{
      "id": "the-key-property"
    }}>{`The key property`}</h3>
    <p>{`The key property is how React identifies which component is which. You’re gonna have a bad time if you pick wrong. Trust me. I spent many hours debugging and contorting and writing workarounds before I realized that basing the key on the index was a Bad Move™. `}<em parentName="p">{`Obviously`}</em>{`, you want the letter to stay constant in each component and the index to change.`}</p>
    <p>{`That’s how x-axis transitions work. You’re moving the letter into a specific place in the alphabet. You’ll see what I mean in a bit.`}</p>
    <h3 {...{
      "id": "reacttransitiongroup"
    }}>{`ReactTransitionGroup`}</h3>
    <p>{`Wrapping our list of `}<inlineCode parentName="p">{`Letter`}</inlineCode>{`s in `}<inlineCode parentName="p">{`ReactTransitionGroup`}</inlineCode>{` lets us do our magic. This is a low-level API from React add-ons, which expands our kingdom of component lifecycle hooks.`}</p>
    <p>{`In addition to knowing when the component mounts, updates, and unmount, we get access to `}<inlineCode parentName="p">{`componentWillEnter`}</inlineCode>{`, `}<inlineCode parentName="p">{`componentWillLeave`}</inlineCode>{`, and a few others. Notice something familiar?`}</p>
    <p><inlineCode parentName="p">{`componentWillEnter`}</inlineCode>{` is the same as d3’s `}<inlineCode parentName="p">{`.enter()`}</inlineCode>{`, `}<inlineCode parentName="p">{`componentWillLeave`}</inlineCode>{` is the same as d3’s `}<inlineCode parentName="p">{`.exit()`}</inlineCode>{`, and `}<inlineCode parentName="p">{`componentWillUpdate`}</inlineCode>{` is the same as d3’s `}<inlineCode parentName="p">{`.update()`}</inlineCode>{`.`}</p>
    <p>{`“The same” is a strong concept – they’re analogous. The key difference is that d3’s hooks operate on entire selections – groups of components – while React’s lifecycle hooks operate on each component individually. In d3, an overlord is dictating what happens; in React, each component knows what to do.`}</p>
    <p>{`That makes React code easier to understand. I think. ¯`}{`\\`}{`_`}{`(ツ)`}{`_`}{`/¯`}</p>
    <p><inlineCode parentName="p">{`ReactTransitionGroup`}</inlineCode>{` gives us `}<a parentName="p" {...{
        "href": "https://facebook.github.io/react/docs/animation.html"
      }}>{`even more hooks`}</a>{`, but these are what we need. It’s nice that in both `}<inlineCode parentName="p">{`componentWillEnter`}</inlineCode>{` and `}<inlineCode parentName="p">{`componentWillLeave`}</inlineCode>{` we can use the callback to explicitly say `}<em parentName="p">{`“The transition is done. React, back to you”`}</em>{`.`}</p>
    <p>{`My thanks to Michelle Tilley for writing about `}<inlineCode parentName="p">{`ReactTransitionGroup`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "http://stackoverflow.com/questions/29977799/how-should-i-handle-a-leave-animation-in-componentwillunmount-in-react"
      }}>{`on StackOverflow`}</a>{`.`}</p>
    <h2 {...{
      "id": "the-letter-component"
    }}>{`The Letter component`}</h2>
    <p>{`Now we’re ready for the real magic – a component that can transition itself into and out of a visualization… without bothering anyone else `}<em parentName="p">{`or`}</em>{` confusing React.`}</p>
    <p>{`The basic skeleton of our `}<inlineCode parentName="p">{`Letter`}</inlineCode>{` component looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/Letter.jsx

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import d3 from 'd3';

class Letter extends Component {
    state = {
        y: -60,
        x: 0,
        className: 'enter',
        fillOpacity: 1e-6
    }
    transition = d3.transition()
                   .duration(750)
                   .ease(d3.easeCubicInOut);

    componentWillEnter(callback) {
        // start enter transition, then callback()
    }

    componentWillLeave(callback) {
        // start exit transition, then callback()
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.i != nextProps.i) {
           // start update transition
        }
    }

    render() {
       // spit out a <text> element
    }
};

export default Letter;</text>
`}</code></pre>
    <p>{`We start with some imports, and define a `}<inlineCode parentName="p">{`Letter`}</inlineCode>{` component with a default state and a default transition. Yes, it feels weird to use `}<inlineCode parentName="p">{`state`}</inlineCode>{` for coordinates, class name, and styling, but the component needs to change them sometimes. As far as I know, components can’t change their own props.`}</p>
    <p>{`Defining a default transition is a d3 v4 trick. It saves us some typing. With the stable version of d3, you’d define this duration and easing function on each transition.`}</p>
    <p>{`All of the magic values – default/final `}<inlineCode parentName="p">{`y`}</inlineCode>{` coordinate, transition properties, etc. – are good candidates for props. That would make `}<inlineCode parentName="p">{`Alphabet`}</inlineCode>{` more flexible, but add unnecessary complexity for this tutorial.`}</p>
    <h3 {...{
      "id": "componentwillenter"
    }}>{`componentWillEnter`}</h3>
    <p>{`We start with the enter transition in `}<inlineCode parentName="p">{`componentWillEnter`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/Letter.jsx

    componentWillEnter(callback) {
        let node = d3.select(ReactDOM.findDOMNode(this));

        this.setState({x: this.props.i*32});

        node.transition(this.transition)
            .attr('y', 0)
            .style('fill-opacity', 1)
            .on('end', () => {
                this.setState({y: 0, fillOpacity: 1});
                callback()
            });
    }
`}</code></pre>
    <p>{`We use `}<inlineCode parentName="p">{`reactDOM.findDOMNode`}</inlineCode>{` to get our DOM node and use `}<inlineCode parentName="p">{`d3.select`}</inlineCode>{` to turn it into a d3 selection. Now we can do any and all d3 magic.`}</p>
    <p>{`Then we update `}<inlineCode parentName="p">{`this.state.x`}</inlineCode>{` the current index and letter width. The width is a value that we Just Know™.`}</p>
    <p>{`We keep our letter’s `}<inlineCode parentName="p">{`x`}</inlineCode>{` coordinate in `}<inlineCode parentName="p">{`this.state`}</inlineCode>{` to avoid jumpiness. The `}<inlineCode parentName="p">{`i`}</inlineCode>{` prop updates on each render, but we want to transition into the new position slowly. You’ll see how that works in the `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{` section.`}</p>
    <p>{`At this point our component is invisible and 60 pixels above the baseline. That’s because of the default values for `}<inlineCode parentName="p">{`fillOpacity`}</inlineCode>{` and `}<inlineCode parentName="p">{`y`}</inlineCode>{` that we set earlier.`}</p>
    <p>{`To animate our component moving down and becoming visible, we use a d3 transition.`}</p>
    <p>{`We use `}<inlineCode parentName="p">{`node.transition(this.transition)`}</inlineCode>{` to start a new transition using the default settings from earlier. In d3 v3, we’d have to define a duration and easing function here manually.`}</p>
    <p>{`Then we define what/how should change with `}<inlineCode parentName="p">{`.attr`}</inlineCode>{` and `}<inlineCode parentName="p">{`.style`}</inlineCode>{`. This operates directly on the DOM and doesn’t tell React about the changes.`}</p>
    <p>{`We can sync React’s imagination with reality in a “transition is over” callback using `}<inlineCode parentName="p">{`.on('end'`}</inlineCode>{`. We use `}<inlineCode parentName="p">{`setState`}</inlineCode>{` to update component state, and trigger the main `}<inlineCode parentName="p">{`callback`}</inlineCode>{`. React now knows this letter is done appearing.`}</p>
    <p>{`In d3 v3, you’d use `}<inlineCode parentName="p">{`.each('end'`}</inlineCode>{` to define the callback.`}</p>
    <h3 {...{
      "id": "componentwillleave"
    }}>{`componentWillLeave`}</h3>
    <p>{`The exit transition goes in `}<inlineCode parentName="p">{`componentWillLeave`}</inlineCode>{` and follows the same principle, except in reverse. It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/Letter.jsx

    componentWillLeave(callback) {
        let node = d3.select(ReactDOM.findDOMNode(this));

        this.setState({className: 'exit'});

        node.transition(this.transition)
            .attr('y', 60)
            .style('fill-opacity', 1e-6)
            .on('end', () => {
                this.setState({y: 60, fillOpacity: 1e-6});
                callback()
            });
    }
`}</code></pre>
    <p>{`This time, we update state to change the `}<inlineCode parentName="p">{`className`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`x`}</inlineCode>{`. That’s because `}<inlineCode parentName="p">{`x`}</inlineCode>{` doesn’t change.`}</p>
    <p>{`The exit transition itself is an inverse of the enter transition. An exiting letter moves further down and becomes invisible. Once the transition is over, we update state for consistency’s sake, and we tell React it can remove the component.`}</p>
    <p>{`On second though, we might not need to update state in this case. The component goes bye-bye anyway…`}</p>
    <h3 {...{
      "id": "componentwillreceiveprops"
    }}>{`componentWillReceiveProps`}</h3>
    <p>{`The update transition goes into `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{` like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/Letter.jsx

    componentWillReceiveProps(nextProps) {
        if (this.props.i != nextProps.i) {
            let node = d3.select(ReactDOM.findDOMNode(this));

            this.setState({className: 'update'});

            node.transition(this.transition)
                .attr('x', nextProps.i*32)
                .on('end', () => this.setState({x: nextProps.i*32}));
        }
    }
`}</code></pre>
    <p>{`You know the pattern by now, don’t you? Update state, do transition, sync state with reality after transition.`}</p>
    <p>{`In this case, we change the `}<inlineCode parentName="p">{`className`}</inlineCode>{`, then we move the letter into its new horizontal position.`}</p>
    <p>{`We could have done all of this in `}<inlineCode parentName="p">{`componentWillUpdate`}</inlineCode>{` as well. However, we can’t do it in `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`. We need to know both the current index `}<em parentName="p">{`and`}</em>{` the new index. It helps us decide whether to transition or not.`}</p>
    <p>{`There are instances when the component updates, but its horizontal position doesn’t change. Every time we call `}<inlineCode parentName="p">{`setState`}</inlineCode>{` for example.`}</p>
    <h3 {...{
      "id": "render"
    }}>{`render`}</h3>
    <p>{`After all that transition magic, you might be thinking `}<em parentName="p">{`“Holy shit, how do I render this!?”`}</em>{`. I don’t blame ya!`}</p>
    <p>{`But we did the hard work. Rendering is straightforward:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// src/components/Alphabet/Letter.jsx

    render() {
        return (
            <text dy=".35em" y={this.state.y} x={this.state.x} class={this.state.className} style="{{fillOpacity:" this.state.fillopacity}}="">
                {this.props.d}
            </text>
        );
    }
`}</code></pre>
    <p>{`We return an SVG `}<inlineCode parentName="p">{`<text>`}</inlineCode>{` element rendered at an `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` position with a `}<inlineCode parentName="p">{`className`}</inlineCode>{` and a `}<inlineCode parentName="p">{`fillOpacity`}</inlineCode>{`. It shows a single letter given by the `}<inlineCode parentName="p">{`d`}</inlineCode>{` prop.`}</p>
    <p>{`As mentioned, using state for `}<inlineCode parentName="p">{`x`}</inlineCode>{`, `}<inlineCode parentName="p">{`y`}</inlineCode>{`, `}<inlineCode parentName="p">{`className`}</inlineCode>{`, and `}<inlineCode parentName="p">{`fillOpacity`}</inlineCode>{` feels weird. It’s the simplest way I know of to communicate between the `}<inlineCode parentName="p">{`render`}</inlineCode>{` and lifecycle methods.`}</p>
    <h2 {...{
      "id": "thats-it"
    }}>{`That’s it`}</h2>
    <p>{`Boom. We’re done.`}</p>
    <p>{`We have an `}<inlineCode parentName="p">{`Alphabet`}</inlineCode>{` component that declaratively renders an animated alphabet. Letters transition in and out and jump left and right.`}</p>
    <p>{`All you need now is a skeleton setup that renders an SVG element and uses the `}<inlineCode parentName="p">{`Alphabet`}</inlineCode>{` component.`}</p>
    <p>{`The key take aways are:`}</p>
    <ul>
      <li parentName="ul">{`use d3 for transitions`}</li>
      <li parentName="ul">{`use React to manage SVG elements`}</li>
      <li parentName="ul">{`use ReactTransitionGroup to get more lifecycle events`}</li>
      <li parentName="ul">{`mimic d3’s enter/update/exit pattern`}</li>
    </ul>
    <p>{`You can learn more about properly integration React and d3js from my book, `}<a parentName="p" {...{
        "href": "http://swizec.com/reactd3js"
      }}>{`React+d3js ES6`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      